import { Box, Flex, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import FaceliftSwitch from '@/components/Atoms/FaceliftSwitch/FaceliftSwitch';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { useDisable2FA, useEnable2FA } from '@/hooks/service-hooks/auth';
import { selectUser, useAuthStore } from '@/store/authStore';
import PasswordConfirmationDialog from './PasswordConfirmationDialog';

const Settings = () => {
	const user = useAuthStore(selectUser);
	const authStore = useAuthStore.getState();
	const [is2FAEnabled, setIs2FAEnabled] = useState(user?.userInformation?.is2FAEnabled ?? false);
	const [showPasswordDialog, setShowPasswordDialog] = useState(false);
	const [pendingAction, setPendingAction] = useState<'enable' | 'disable' | null>(null);

	useEffect(() => {
		setIs2FAEnabled(user?.userInformation?.is2FAEnabled ?? false);
	}, [user?.userInformation?.is2FAEnabled]);

	const { mutate: enable2FAMutation, isLoading: isEnabling } = useEnable2FA(
		(response) => {
			setIs2FAEnabled(true);
			authStore.updateUserInformation({
				is2FAEnabled: true,
			});
			toast.success(response.message || 'Two-factor authentication has been enabled successfully');
			setShowPasswordDialog(false);
			setPendingAction(null);
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message || 'Failed to enable 2FA. Please check your password and try again.',
			);
		},
	);

	const { mutate: disable2FAMutation, isLoading: isDisabling } = useDisable2FA(
		(response) => {
			setIs2FAEnabled(false);
			authStore.updateUserInformation({
				is2FAEnabled: false,
			});
			toast.success(response.message || 'Two-factor authentication has been disabled');
			setShowPasswordDialog(false);
			setPendingAction(null);
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message || 'Failed to disable 2FA. Please check your password and try again.',
			);
		},
	);

	const handle2FAToggle = (checked: boolean) => {
		setPendingAction(checked ? 'enable' : 'disable');
		setShowPasswordDialog(true);
	};

	const handlePasswordConfirm = (password: string) => {
		const email = user?.userInformation?.email;
		
		if (!email) {
			toast.error('User email not found. Please try logging in again.');
			return;
		}

		if (pendingAction === 'enable') {
			enable2FAMutation({ email, password });
		} else if (pendingAction === 'disable') {
			disable2FAMutation({ email, password });
		}
	};

	const handleDialogClose = () => {
		setShowPasswordDialog(false);
		setPendingAction(null);
	};

	return (
		<Box width="100%">
			<Flex direction="column" gap={6}>
				<Box>
					<Text fontWeight="bold" fontSize="lg" mb={4}>
						Two-Factor Authentication (2FA)
					</Text>

					<Flex direction="column" gap={4}>
						<Box
							p={4}
							bg="brand.cardBackground"
							borderRadius="md"
							border="1px solid"
							borderColor={{ _dark: 'gray.700', _light: 'gray.200' }}
						>
							<FaceliftText
								fontSize="sm"
								fontWeight="semibold"
								color="brand.textDark"
								mb={1}
							>
								Method: Email
							</FaceliftText>
							<FaceliftText fontSize="xs" color="brand.textDark" opacity={0.8}>
								Verification codes will be sent to your registered email address
							</FaceliftText>
						</Box>

						<Flex
							direction="column"
							gap={3}
							p={4}
							bg="brand.cardBackground"
							borderRadius="md"
							border="1px solid"
							borderColor={{ _dark: 'gray.700', _light: 'gray.200' }}
						>
							<Flex align="center" justifyContent="space-between">
								<Flex direction="column" gap={1} flex={1}>
									<FaceliftText
										fontSize="sm"
										fontWeight="medium"
										color="brand.textDark"
									>
										Enable Two-Factor Authentication
									</FaceliftText>
									<FaceliftText fontSize="xs" color="brand.textDark" opacity={0.8}>
										Add an extra layer of security to your account
									</FaceliftText>
								</Flex>
								<FaceliftSwitch
									checked={is2FAEnabled}
									onCheckedChange={handle2FAToggle}
								/>
							</Flex>

							{is2FAEnabled ? (
								<Box
									p={3}
									bg={{ _dark: 'green.900', _light: 'green.50' }}
									borderRadius="md"
									border="1px solid"
									borderColor={{ _dark: 'green.700', _light: 'green.200' }}
								>
									<FaceliftText fontSize="xs" color={{ _dark: 'green.200', _light: 'green.800' }}>
										✓ Two-factor authentication is enabled. You'll receive a verification
										code via email each time you log in from a new device or location.
									</FaceliftText>
								</Box>
							) : (
								<Box
									p={3}
									bg={{ _dark: 'yellow.900', _light: 'yellow.50' }}
									borderRadius="md"
									border="1px solid"
									borderColor={{ _dark: 'yellow.700', _light: 'yellow.200' }}
								>
									<FaceliftText fontSize="xs" color={{ _dark: 'yellow.200', _light: 'yellow.800' }}>
										⚠ Two-factor authentication is disabled. We recommend enabling this
										feature to protect your account from unauthorized access.
									</FaceliftText>
								</Box>
							)}
						</Flex>
					</Flex>
				</Box>
			</Flex>

			<PasswordConfirmationDialog
				open={showPasswordDialog}
				onOpenChange={handleDialogClose}
				onConfirm={handlePasswordConfirm}
				title={
					pendingAction === 'enable'
						? 'Enable Two-Factor Authentication'
						: 'Disable Two-Factor Authentication'
				}
				description={
					pendingAction === 'enable'
						? 'To enhance your account security, please enter your password to enable two-factor authentication.'
						: 'To disable two-factor authentication, please enter your password to confirm this action.'
				}
				isLoading={isEnabling || isDisabling}
			/>
		</Box>
	);
};

export default Settings;
